import { Postcode } from '@dreryk/address-common-api';
import { FileDescriptor } from '@dreryk/command-query-ws-api';
import {
   HealthcareOrganization,
   HealthcareOrganizationRegisteringAuthority,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { EmailAddress } from '@dreryk/email-address-common-api';
import { HealthcareProviderRegisterBookNumber } from '@dreryk/healthcare-provider-register-book-number-api';
import { PhoneNumber } from '@dreryk/phone-number-common-api';

import { createFileDescriptorFromBase64, FormContextValues, useFormController } from '@dreryk/react-common';
import { AutoLayout, FormRouteLeavingPrompt, FormValidationSnackbar } from '@dreryk/react-components';

import { healthcareOrganizationTranslationKeys } from '../../../translations';
import { CityInput } from '../../common/CityInput';
import { EmailAddressInput } from '../../common/EmailAddressInput';
import { FlatInput } from '../../common/FlatInput';
import { HealthcareProviderNameInput } from '../../common/HealthcareProviderNameInput';
import { HouseInput } from '../../common/HouseInput';
import { LogoFileUpload } from '../../common/LogoFileUpload/LogoFileUpload';
import { PhoneNumberInput } from '../../common/PhoneNumberInput';
import { PostcodeInput } from '../../common/PostcodeInput';
import { RegisterBookNumberInput } from '../../common/RegisterBookNumberInput';
import { RegonInput } from '../../common/RegonInput';
import { StreetInput } from '../../common/StreetInput';
import { TaxIdentifierInput } from '../../common/TaxIdentifierInput';

import { HealthcareOrganizationUpdateAction } from './HealthcareOrganizationUpdateAction';
import { RegisteringAuthoritySelect } from './RegisteringAuthoritySelect';

export type HealthcareOrganizationUpdateFormBean = {
   name: string;
   registerBookNumber: HealthcareProviderRegisterBookNumber | null;
   registeringAuthority: HealthcareOrganizationRegisteringAuthority | null;
   city: string | null;
   postcode: Postcode | null;
   street: string | null;
   house: string | null;
   flat: string | null;
   phoneNumber: PhoneNumber | null;
   emailAddress: EmailAddress | null;
   regon: string | null;
   taxIdentifier: string | null;
   logo: FileDescriptor | null;
};

const getInitialValues = (healthcareOrganization: HealthcareOrganization): HealthcareOrganizationUpdateFormBean => {
   const { logo } = healthcareOrganization;
   return {
      name: healthcareOrganization.name,
      registerBookNumber: healthcareOrganization.registerBookNumber ?? null,
      registeringAuthority: healthcareOrganization.registeringAuthority ?? null,
      city: healthcareOrganization.address.findTown()?.getName() ?? null,
      postcode: healthcareOrganization.address.findPostcode() ?? null,
      street: healthcareOrganization.address.findStreet()?.asString() ?? null,
      house: healthcareOrganization.address.findBuilding()?.asString() ?? null,
      flat: healthcareOrganization.address.findFlat()?.asString() ?? null,
      phoneNumber: healthcareOrganization.phoneNumber,
      emailAddress: healthcareOrganization.emailAddress ?? null,
      regon: healthcareOrganization.regon?.asString() ?? null,
      taxIdentifier: healthcareOrganization.taxIdentifier.asString() ?? null,
      logo: logo ? createFileDescriptorFromBase64(logo.data, logo.fileName, logo.mimeType) : null,
   };
};

export type HealthcareOrganizationUpdateFormController = FormContextValues<HealthcareOrganizationUpdateFormBean>;

type HealthcareOrganizationUpdateFormProps = {
   readonly healthcareOrganization: HealthcareOrganization;
   readonly onDataChanged: VoidFunction;
};

// eslint-disable-next-line max-lines-per-function
export const HealthcareOrganizationUpdateForm = (props: HealthcareOrganizationUpdateFormProps): JSX.Element => {
   const { register, handleSubmit, formState }: HealthcareOrganizationUpdateFormController = useFormController(
      getInitialValues(props.healthcareOrganization)
   );

   return (
      <>
         <AutoLayout as={'form'} noValidate $direction={'vertical'} $spacing={24} $horizontalResizing={697}>
            <AutoLayout as={'div'} $spacing={30} $horizontalResizing={600}>
               <AutoLayout
                  as={RegisterBookNumberInput}
                  name={'registerBookNumber'}
                  register={register}
                  required
                  $horizontalResizing={200}
               />
               <AutoLayout
                  as={RegisteringAuthoritySelect}
                  name={'registeringAuthority'}
                  register={register}
                  required
                  $horizontalResizing={390}
               />
            </AutoLayout>
            <HealthcareProviderNameInput name={'name'} register={register} required />
            <AutoLayout as={'div'} $spacing={30} $horizontalResizing={563}>
               <AutoLayout as={CityInput} name={'city'} register={register} required $horizontalResizing={429} />
               <AutoLayout
                  as={PostcodeInput}
                  name={'postcode'}
                  register={register}
                  required
                  $horizontalResizing={104}
               />
            </AutoLayout>
            <AutoLayout as={'div'} $spacing={30}>
               <AutoLayout as={StreetInput} name={'street'} register={register} $horizontalResizing={429} />
               <AutoLayout as={HouseInput} name={'house'} register={register} required $horizontalResizing={104} />
               <AutoLayout as={FlatInput} name={'flat'} register={register} $horizontalResizing={104} />
            </AutoLayout>
            <AutoLayout as={'div'} $spacing={30} $horizontalResizing={570}>
               <AutoLayout
                  as={PhoneNumberInput}
                  name={'phoneNumber'}
                  register={register}
                  required
                  $horizontalResizing={232}
               />
               <AutoLayout as={EmailAddressInput} name={'emailAddress'} register={register} $horizontalResizing={308} />
            </AutoLayout>
            <AutoLayout as={'div'} $spacing={30} $horizontalResizing={390}>
               <AutoLayout
                  as={RegonInput}
                  name={'regon'}
                  register={register}
                  required
                  type={'regon'}
                  $horizontalResizing={180}
               />
               <AutoLayout
                  as={TaxIdentifierInput}
                  name={'taxIdentifier'}
                  register={register}
                  required
                  $horizontalResizing={180}
               />
            </AutoLayout>
            <AutoLayout as={'div'} $spacing={30} $horizontalResizing={505}>
               <AutoLayout as={LogoFileUpload} name={'logo'} register={register} $horizontalResizing={431} />
            </AutoLayout>
         </AutoLayout>

         <HealthcareOrganizationUpdateAction handleSubmit={handleSubmit} onDataChanged={props.onDataChanged} />
         <FormRouteLeavingPrompt
            formState={formState}
            title={healthcareOrganizationTranslationKeys.leavingPrompt.title}
            message={healthcareOrganizationTranslationKeys.leavingPrompt.message}
         />
         <FormValidationSnackbar formState={formState} />
      </>
   );
};
